// Episode list as a bottom sheet (phone) or centred panel (desktop). Used by the episode player and by Reels.
// - Slides up with the drawer curve, slides down on close (faster than it opens).
// - Drag down or flick to dismiss (only when the list is scrolled to the top), with friction when pulled up.
// - Focus moves to Close on open and back to the button that opened it on close; Escape closes.
import { useCallback, useEffect, useRef, useState, type CSSProperties, type RefObject, type TouchEvent } from 'react';
import { clock } from '../lib/format';
import { Icon } from './Icon';

export interface SheetEpisode { id: string; no: number; title: string; durationS: number }

export function useSheet(returnFocus: RefObject<HTMLElement | null>) {
  const [state, setState] = useState<'open' | 'closing' | null>(null);
  const open = useCallback(() => setState('open'), []);
  const close = useCallback((restoreFocus = true) => {
    setState((st) => (st === 'open' ? 'closing' : st));
    window.setTimeout(() => { setState(null); if (restoreFocus) returnFocus.current?.focus(); }, 180);
  }, [returnFocus]);
  const dismissNow = useCallback(() => setState(null), []);
  useEffect(() => {
    if (state !== 'open') return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);
  return { state, open, close, dismissNow };
}

export function EpisodeSheet({ title, color, episodes, currentNo, isLocked, closing, onClose, onPick }: {
  title: string;
  color: string;
  episodes: SheetEpisode[];
  currentNo?: number;
  isLocked: (no: number) => boolean;
  closing: boolean;
  onClose: () => void;
  onPick: (no: number) => void;
}) {
  const sheetEl = useRef<HTMLDivElement>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ y: number; t: number; dy: number } | null>(null);
  useEffect(() => { closeBtn.current?.focus(); }, []);

  const onTouchStart = (ev: TouchEvent) => {
    const el = sheetEl.current;
    if (!el || el.scrollTop > 0) return;
    drag.current = { y: ev.touches[0].clientY, t: performance.now(), dy: 0 };
  };
  const onTouchMove = (ev: TouchEvent) => {
    const d = drag.current; const el = sheetEl.current;
    if (!d || !el) return;
    const dy = ev.touches[0].clientY - d.y;
    d.dy = dy > 0 ? dy : dy * 0.2;
    el.style.transform = `translateY(${d.dy}px)`;
  };
  const onTouchEnd = () => {
    const d = drag.current; const el = sheetEl.current;
    drag.current = null;
    if (!d || !el) return;
    const v = Math.abs(d.dy) / Math.max(1, performance.now() - d.t);
    if (d.dy > 120 || (d.dy > 16 && v > 0.11)) { onClose(); return; }
    el.style.transition = 'transform 220ms cubic-bezier(0.23, 1, 0.32, 1)';
    el.style.transform = '';
    window.setTimeout(() => { el.style.transition = ''; }, 240);
  };

  return (
    <div className={'ep-sheet-backdrop' + (closing ? ' closing' : '')} onClick={onClose}>
      <div className="ep-sheet" ref={sheetEl} role="dialog" aria-modal="true" aria-label={`Episodes of ${title}`} onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}>
        <span className="sheet-grip" aria-hidden="true" />
        <div className="ep-sheet-head">
          <span><b>{title}</b><small className="muted"> · {episodes.length} episodes</small></span>
          <button type="button" ref={closeBtn} className="icon-btn" onClick={onClose} aria-label="Close"><Icon name="close" /></button>
        </div>
        <ol className="episodes compact">
          {episodes.map((x) => {
            const locked = isLocked(x.no);
            const now = x.no === currentNo;
            return (
              <li key={x.id}>
                <button type="button" className={'episode' + (now ? ' now' : '')} aria-current={now ? 'true' : undefined} onClick={() => onPick(x.no)}>
                  <span className="ep-thumb" style={{ '--poster': color } as CSSProperties}>{x.no}</span>
                  <span className="ep-text"><b>{x.title}</b><small>{clock(x.durationS)}{now ? ' · Playing now' : ''}</small></span>
                  {locked ? <span className="tag-plus"><Icon name="lock" size={14} /> PLUS</span> : null}
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
